import {createAsyncThunk} from '@reduxjs/toolkit';
import {getJWTToken} from '../../lib/auth-token';
import {handleAuthFailure, signOutExpiredSession} from '../../lib/auth-error-handler';
import {getWordlistApiUrl, apiFetch} from '../../lib/api';
import {ApiError, toApiError, toNetworkError} from '../../lib/apiError';
import {NOT_ADMIN_CODE} from './adminThunks';
import type {
  AdminWordlistDetailResponse,
  AdminWordlistListResponse,
  AdminWordlistWriteResponse,
} from '../../backend-types/wordlist-admin-api';
import type {
  AdminPage,
  AdminWordlist,
  AdminWordlistCreateInput,
  AdminWordlistDetail,
  AdminWordlistQuery,
  AdminWordlistUpdateInput,
} from '../../domain/adminWordlists';
import {toAdminWordlist, toAdminWordlistDetail, toAdminWordlistPage} from '../../domain/adminWordlistMappers';

/**
 * The admin wordlist catalog: every list across every user, editable regardless of owner.
 *
 * This hits the **Wordlist Worker** (`getWordlistApiUrl()`), not the main worker `adminThunks.ts`
 * talks to — `/admin/wordlists/*` is a separate admin surface bolted onto word-list-worker/index.js,
 * with its own admin gate. Same 404-means-not-admin convention, so `NOT_ADMIN_CODE` is reused from
 * `adminThunks.ts` rather than redefined, which keeps `adminSlice`'s single `accessDenied` matcher
 * (any `admin/*` rejection carrying that code) working for these thunks too — hence the `admin/`
 * action-type prefix below, matching the main admin thunks rather than a `adminWordlists/` prefix.
 *
 * **There is no hard delete.** DELETE flips `is_deleted`; the only way back is
 * `updateAdminWordlist({id, isDeleted: false})`, because `conversions.wordlist_id` references the
 * row and a past conversion's record of what censored it has to survive the list being retired.
 */

interface AdminWordlistRequest {
  path: string;
  method?: string;
  body?: unknown;
  fallbackMessage: string;
}

/** Same shape and 404-recognition as `adminRequest` in `adminThunks.ts`, pointed at the other worker. */
async function adminWordlistRequest<TWire>(
  {path, method = 'GET', body, fallbackMessage}: AdminWordlistRequest,
  dispatch: (action: any) => unknown
): Promise<{ok: true; data: TWire} | {ok: false; error: ApiError}> {
  try {
    const jwt = await getJWTToken();

    if (!jwt) {
      return {ok: false, error: {status: 401, message: 'No authentication token available'}};
    }

    const headers: Record<string, string> = {Authorization: `Bearer ${jwt}`};
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await apiFetch(`${getWordlistApiUrl()}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();

      if (response.status === 401) {
        signOutExpiredSession(dispatch);
      }

      if (response.status === 404) {
        // Not "no such wordlist" — the admin surface hides itself from non-admins. A genuinely
        // missing id and a revoked admin are indistinguishable to the client; that's the trade.
        return {
          ok: false,
          error: {status: 404, message: 'This account is not an administrator.', code: NOT_ADMIN_CODE},
        };
      }

      return {ok: false, error: toApiError(response.status, errorText, fallbackMessage)};
    }

    return {ok: true, data: (await response.json()) as TWire};
  } catch (error) {
    handleAuthFailure(error, dispatch);
    return {ok: false, error: toNetworkError(error, fallbackMessage)};
  }
}

const buildQuery = (params: Record<string, string | number | boolean | undefined>): string => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });

  const query = search.toString();
  return query ? `?${query}` : '';
};

/* ==========================================================================
 * List
 * ========================================================================== */

export const fetchAdminWordlists = createAsyncThunk<
  AdminPage<AdminWordlist>,
  AdminWordlistQuery,
  {rejectValue: ApiError}
>('admin/fetchWordlists', async (query, {dispatch, rejectWithValue}) => {
  const result = await adminWordlistRequest<AdminWordlistListResponse>(
    {
      path: `/admin/wordlists${buildQuery({
        search: query.search,
        is_official: query.isOfficial || undefined,
        is_public: query.isPublic || undefined,
        user_id: query.userId,
        include_deleted: query.includeDeleted ? 'true' : undefined,
        order: query.order,
        limit: query.limit,
        offset: query.offset,
      })}`,
      fallbackMessage: 'Could not load word lists',
    },
    dispatch
  );

  return result.ok ? toAdminWordlistPage(result.data) : rejectWithValue(result.error);
});

/* ==========================================================================
 * Detail
 * ========================================================================== */

export const fetchAdminWordlistDetail = createAsyncThunk<
  AdminWordlistDetail,
  string,
  {rejectValue: ApiError}
>('admin/fetchWordlistDetail', async (wordlistId, {dispatch, rejectWithValue}) => {
  const result = await adminWordlistRequest<AdminWordlistDetailResponse>(
    {
      path: `/admin/wordlists/${encodeURIComponent(wordlistId)}`,
      fallbackMessage: 'Could not load this word list',
    },
    dispatch
  );

  return result.ok ? toAdminWordlistDetail(result.data) : rejectWithValue(result.error);
});

/* ==========================================================================
 * Create
 * ========================================================================== */

export const createAdminWordlist = createAsyncThunk<
  AdminWordlist,
  AdminWordlistCreateInput,
  {rejectValue: ApiError}
>('admin/createWordlist', async (input, {dispatch, rejectWithValue}) => {
  const result = await adminWordlistRequest<AdminWordlistWriteResponse>(
    {
      path: '/admin/wordlists',
      method: 'POST',
      body: {
        name: input.name,
        description: input.description,
        words: input.words,
        isPublic: input.isPublic,
        isOfficial: input.isOfficial,
        userId: input.userId,
      },
      fallbackMessage: 'Could not create the word list',
    },
    dispatch
  );

  return result.ok ? toAdminWordlist(result.data.wordlist) : rejectWithValue(result.error);
});

/* ==========================================================================
 * Update (and restore: { isDeleted: false })
 * ========================================================================== */

export const updateAdminWordlist = createAsyncThunk<
  AdminWordlist,
  AdminWordlistUpdateInput,
  {rejectValue: ApiError}
>('admin/updateWordlist', async ({id, ...fields}, {dispatch, rejectWithValue}) => {
  const result = await adminWordlistRequest<AdminWordlistWriteResponse>(
    {
      path: `/admin/wordlists/${encodeURIComponent(id)}`,
      method: 'PUT',
      body: fields,
      fallbackMessage: 'Could not update this word list',
    },
    dispatch
  );

  return result.ok ? toAdminWordlist(result.data.wordlist) : rejectWithValue(result.error);
});

/* ==========================================================================
 * Delete (soft)
 * ========================================================================== */

export const deleteAdminWordlist = createAsyncThunk<
  AdminWordlist,
  string,
  {rejectValue: ApiError}
>('admin/deleteWordlist', async (wordlistId, {dispatch, rejectWithValue}) => {
  const result = await adminWordlistRequest<AdminWordlistWriteResponse>(
    {
      path: `/admin/wordlists/${encodeURIComponent(wordlistId)}`,
      method: 'DELETE',
      fallbackMessage: 'Could not delete this word list',
    },
    dispatch
  );

  return result.ok ? toAdminWordlist(result.data.wordlist) : rejectWithValue(result.error);
});
